export class MatchEventDto {
  constructor(event) {
    if (new.target === MatchEventDto) {
      throw new Error("MatchEventDto est une classe abstraite et ne peut pas être instanciée directement.");
    }

    this._id = event._id
    this.idMatch = event.idMatch;
    // this.type = event.type;

    // Définir `type` sans casser les enfants qui ont un getter
    const proto = Object.getPrototypeOf(this);
    const desc = Object.getOwnPropertyDescriptor(proto, 'type');

    // Si la sous-classe a un accesseur (getter/setter), on NE TOUCHE PAS
    const subclassHasAccessor = !!(desc && (desc.get || desc.set));

    if (!subclassHasAccessor && !Object.prototype.hasOwnProperty.call(this, 'type')) {
      Object.defineProperty(this, 'type', {
        value: event.type,
        writable: true,
        enumerable: true,
        configurable: true,
      });
    }


    this.creation = event.creation ?? new Date()
  }
}

function normalizeDisplayDate(v) {
  if (v == null || v === '') return undefined; // laisse Mongoose gérer undefined
  if (v instanceof Date && !isNaN(v)) return new Date(v); // clone sûr
  if (typeof v === 'string') {
    const d = new Date(v);
    return isNaN(d) ? undefined : d;
  }
  // dayjs/moment-like
  if (v && typeof v === 'object') {
    if (typeof v.toDate === 'function') {
      const d = v.toDate();
      return isNaN(d) ? undefined : d;
    }
    if (typeof v.valueOf === 'function') {
      const d = new Date(v.valueOf());
      return isNaN(d) ? undefined : d;
    }
  }
  return undefined;
}

export class NotationEventDto extends MatchEventDto {
  constructor(event = {}) {
    super(event);
    this.tab = event.tab ?? 'NOTATION';
    this.customQuestion = event.customQuestion ?? '';
    this.globalTarget = event.globalTarget ?? '';   // ex: 'player' | 'team' | 'global'
    this.preciseTarget = event.preciseTarget ?? ''; // ex: 'team' | 'line' | 'player'
    this.exactTarget = event.exactTarget ?? [];     // ex: ids ou objets {id,name}

    this.timer = event.timer ?? 0;
    this.period = event.period ?? 0;
    this.votes = event.votes ?? [];
    this.details = event.details ?? [];

    this.tags = event.tags ?? [];
    this.displayDate = event.displayDate ?? undefined;
    this.isPin = event.isPin ?? false;
  }

  initTimerPeriod(match) {
    if (match.type === 'actu') {
      this.timer = 0;
      this.period = 0;
    } else {
      this.timer = (match.timer === 0 && match.statusTimer === 'off')
        ? 0
        : parseInt(match.timer / 60) * 60 + 60;
      this.period = parseInt(match.period);
    }
  }

  // ✅ immuable : retourne une NOUVELLE instance
  updateDetail(key, value) {
    if (!(key in this)) {
      console.warn(`La clé "${key}" n'existe pas dans NotationEventDto.`);
      return this;
    }

    const next = Object.assign(
      Object.create(Object.getPrototypeOf(this)),
      this
    );

    const clone = (v) =>
      Array.isArray(v)
        ? v.map(it => (it && typeof it === 'object') ? { ...it } : it)
        : (v && typeof v === 'object')
          ? { ...v }
          : v;

    next[key] = clone(value);

    if (key === 'displayDate') {
      next[key] = normalizeDisplayDate(value);
    }

    // 🏎️ Règles auto (sur la NOUVELLE instance)
    if (key === 'period') {
      next.timer = 0;
    }
    if (key === 'globalTarget') {
      next.preciseTarget = 'team'; // règle métier actuelle
      next.exactTarget = [];       // reset
    }
    if (key === 'preciseTarget') {
      next.exactTarget = [];       // reset
    }

    return next;
  }
}

export class SurveyEventDto extends MatchEventDto {
  constructor(event) {
    super(event);
    this.tab = event.tab ?? 'SONDAGE'
    this.question = event.question ?? '';
    this.choices = event.choices ?? [];
    this.options = event.options ?? {
      numberOfChoices : 1,
      validate : false,
    };
    this.timer = event.timer ?? 0;
    this.period = event.period ?? 0;
    this.votes = event.votes ?? []

    this.displayDate = event.displayDate ?? undefined

    this.tags = event.tags ?? []

    this.isPin = event.isPin ?? false
  }

  get numberOfChoices() {
    return this.options.numberOfChoices;
  }
  set numberOfChoices(value) {
    this.options.numberOfChoices = value;
  }

  get validate() {
    return this.options.validate;
  }
  set validate(value) {
    this.options.validate = value;
  }

  initTimerPeriod(match){
    if(match.type === 'actu'){
      this.timer = 0
      this.period = 0
    }else{
      this.timer = (match.timer === 0 && match.statusTimer==="off") ? 0 : parseInt(match.timer / 60) * 60 + 60
      this.period = parseInt(match.period)
    }
  }

  // 🔄 Met à jour dynamiquement une valeur dans `details`
  updateDetail(key, value) {

    if (!(key in this)) {
    console.warn(`La clé "${key}" n'existe pas dans SurveyEventDto.`);
    return this; // pas de changement
  }

  // on clone l'instance (copie superficielle des props, on garde le même prototype)
  const next = Object.assign(
    Object.create(Object.getPrototypeOf(this)),
    this
  );

  // clone défensif basique
    const cloneDeepChoices = (choices) =>
      choices.map(c => ({
        ...c,
        entries: (c.entries ?? []).map(e => ({ ...e }))
      }));

    let clonedValue = value;
    if (key === 'choices' && Array.isArray(value)) {
      clonedValue = cloneDeepChoices(value);
    } else if (Array.isArray(value)) {
      clonedValue = [...value];
    } else if (value && typeof value === 'object') {
      clonedValue = { ...value };
    }

  next[key] = clonedValue;

  if (key === 'displayDate') {
      next[key] = normalizeDisplayDate(value);
    }

   // 🏎️ **Règles automatiques**
    if (key === "period") {
      this.timer = 0; // 🔄 Si la période change, on reset le timer
    }

  return next;
   
  }
}

export class QuizzEventDto extends MatchEventDto {
  constructor(event) {
    super(event);
    this.tab = event.tab ?? 'QUIZ';
    this.question = event.question ?? '';
    this.choices = event.choices ?? [];
    this.options = event.options ?? {
      numberOfChoices: 1,
      validate: false,
      multipleChoice: true,
    };
    this.timer = event.timer ?? 0;
    this.period = event.period ?? 0;
    this.votes = event.votes ?? [];

    this.displayDate = event.displayDate ?? undefined;
    this.isPin = event.isPin ?? false;
    this.tags = event.tags ?? [];
  }

  get numberOfChoices() { return this.options.numberOfChoices; }
  set numberOfChoices(value) { this.options.numberOfChoices = value; }

  get validate() { return this.options.validate; }
  set validate(value) { this.options.validate = value; }

  initTimerPeriod(match){
    if (match.type === 'actu') {
      this.timer = 0;
      this.period = 0;
    } else {
      this.timer = (match.timer === 0 && match.statusTimer === "off")
        ? 0
        : parseInt(match.timer / 60) * 60 + 60;
      this.period = parseInt(match.period);
    }
  }

  // ✅ immuable : retourne un NOUVEL objet (même prototype)
  updateDetail(key, value) {

    if (!(key in this)) {
      console.warn(`La clé "${key}" n'existe pas dans QuizzEventDto.`);
      return this;
    }

    const next = Object.assign(
      Object.create(Object.getPrototypeOf(this)),
      this
    );

    // clone défensif basique
    const cloneDeepChoices = (choices) =>
      choices.map(c => ({
        ...c,
        entries: (c.entries ?? []).map(e => ({ ...e }))
      }));

    let clonedValue = value;
    if (key === 'choices' && Array.isArray(value)) {
      clonedValue = cloneDeepChoices(value);
    } else if (Array.isArray(value)) {
      clonedValue = [...value];
    } else if (value && typeof value === 'object') {
      clonedValue = { ...value };
    }


    next[key] = clonedValue;

    if (key === 'displayDate') {
      next[key] = normalizeDisplayDate(value);
    }

    // 🏎️ règles auto
    if (key === 'period') {
      next.timer = 0;
    }

    return next;
  }
}

export class QuestionBoxEventDto extends MatchEventDto {
  constructor(event) {
    super(event);
    this.tab = event.tab ?? 'BOÎTE À QUESTIONS';
    this.title = event.title ?? '';
    this.questions = event.questions ?? []; // souvent [{ id, text }] ou strings
    this.timer = event.timer ?? 0;
    this.period = event.period ?? 0;

    this.displayDate = event.displayDate ?? undefined;
    this.isPin = event.isPin ?? false;
    this.tags = event.tags ?? [];
  }

  initTimerPeriod(match) {
    if (match.type === 'actu') {
      this.timer = 0;
      this.period = 0;
    } else {
      this.timer = (match.timer === 0 && match.statusTimer === "off")
        ? 0
        : parseInt(match.timer / 60) * 60 + 60;
      this.period = parseInt(match.period);
    }
  }

  // ✅ Immuable: retourne une NOUVELLE instance
  updateDetail(key, value) {
    if (!(key in this)) {
      console.warn(`La clé "${key}" n'existe pas dans QuestionBoxEventDto.`);
      return this;
    }

    // clone "même prototype, nouvelles refs"
    const next = Object.assign(
      Object.create(Object.getPrototypeOf(this)),
      this
    );

    // Clone défensif selon le type
    const cloneValue = (v) => {
      if (Array.isArray(v)) {
        // questions est un tableau de strings ou d'objets — on couvre les deux
        return v.map(item => (item && typeof item === 'object') ? { ...item } : item);
      }
      if (v && typeof v === 'object') return { ...v };
      return v;
    };

    next[key] = cloneValue(value);

    if (key === 'displayDate') {
      next[key] = normalizeDisplayDate(value);
    }

    // 🏎️ Règles auto
    if (key === 'period') {
      next.timer = 0;
    }

    return next;
  }
}

export class ArticleDto extends MatchEventDto {
    constructor(event = {}) {
      super(event);
      this.tab = event.tab ?? 'ARTICLE';
      this.title = event.title ?? '';        // ✅ défaut
      this.content = event.content ?? '';    // ✅ défaut
      this.timer = event.timer ?? 0;
      this.period = event.period ?? 0;

      this.options = event.options ?? {};    // ✅ défaut
      this.displayDate = event.displayDate ?? undefined;
      this.isPin = event.isPin ?? false;
      this.tags = event.tags ?? [];
    }

    initTimerPeriod(match){
      if (match.type === 'actu') {
        this.timer = 0;
        this.period = 0;
      } else {
        this.timer = (match.timer === 0 && match.statusTimer === "off")
          ? 0
          : parseInt(match.timer / 60) * 60 + 60;
        this.period = parseInt(match.period);
      }
      // console.log('ArticleDto initTimerPeriod:', this.timer, this.period);
    }

    // ✅ immuable : retourne une NOUVELLE instance (même prototype)
    updateDetail(key, value) {
      if (!(key in this)) {
        console.warn(`La clé "${key}" n'existe pas dans ArticleDto.`);
        return this;
      }
      const next = Object.assign(Object.create(Object.getPrototypeOf(this)), this);

      // clone défensif simple (content/title = primitifs → pas besoin de deep clone)
      const cloned =
        Array.isArray(value) ? [...value] :
        (value && typeof value === 'object') ? { ...value } :
        value;

      next[key] = cloned;

      if (key === 'displayDate') {
        next[key] = normalizeDisplayDate(value);
      }

      // Règles auto
      if (key === 'period') next.timer = 0;

      return next;
    }
}
  